'use client';

import * as React from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu';
import { Button } from 'app/components/ui/button';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';

export function CourseQuestionAndAnswer() {
  const qaData = [
    {
      question: 'What is React used for?',
      answer:
        'React is used for building interactive user interfaces in web applications.',
      totalReplies: 10,
      upvotes: 25,
    },
    {
      question: 'How does Node.js handle asynchronous operations?',
      answer:
        'Node.js uses an event-driven, non-blocking I/O model to handle asynchronous operations efficiently.',
      totalReplies: 8,
      upvotes: 18,
    },
    {
      question:
        'What is the difference between frontend and backend development?',
      answer:
        'Frontend development focuses on the user interface, while backend development handles server-side logic and databases.',
      totalReplies: 15,
      upvotes: 30,
    },
  ];

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'question',
      header: 'Question',
      cell: ({ row }) => <div>{row.getValue('question')}</div>,
    },
    {
      accessorKey: 'answer',
      header: 'Answer',
      cell: ({ row }) => <div>{row.getValue('answer')}</div>,
    },
    {
      accessorKey: 'totalReplies',
      header: 'Total Replies',
      cell: ({ row }) => <div>{row.getValue('totalReplies')}</div>,
    },
    {
      accessorKey: 'upvotes',
      header: 'Upvotes',
      cell: ({ row }) => <div>{row.getValue('upvotes')}</div>,
    },

    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button variant={'ghost'}>
                <DotsHorizontalIcon className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Edit</DropdownMenuItem>
              <DropdownMenuItem>Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: qaData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="w-full rounded-[10px] p-5 border border-solid dark:border-white">
      <h2 className="text-xl font-semibold mb-4">Course Questions & Answers</h2>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No questions available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
